import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ChatRoom, SendMessageRequest } from 'amazon-ivs-chat-messaging';
import HomeNavBar from '../HomePage/HomeNavBar';
import LeftSidebar from '../HomePage/LeftSidebar';
import VideoRequestButton from './VideoRequestButton';
import communityApi from '../../services/communityApi';
import streamApi from '../../services/streamApi';
import { authApi, clearStoredAuth, readStoredToken } from '../../services/authApi';
import { readStoredUser, useNavbarUser } from '../../utils/navbarUser';
import { ROUTES } from '../../constants';
import '../ProfilePage/LiveStreamBlock.css';
import './WatchPage.css';

const WATCH_HEARTBEAT_MS = 15000;
const LIVE_STATUS_POLL_MS = 10000; // poll while offline so page updates when streamer goes live
const PER_USER_CHATS_ENABLED =
  String(process.env.REACT_APP_ENABLE_PER_USER_CHATS).toLowerCase() === 'true';

function WatchPage() {
  const { username } = useParams();
  const navigate = useNavigate();
  const navbarUser = useNavbarUser({ notificationCount: 0 });
  const currentUser = readStoredUser();

  const [profile, setProfile] = useState(null);
  const [liveStatus, setLiveStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [viewerCount, setViewerCount] = useState(0);
  const [viewerLocalStream, setViewerLocalStream] = useState(null);

  const isOwnStream = currentUser?.username && currentUser.username === username;
  const parentHost = typeof window !== 'undefined' ? window.location.hostname : 'localhost';
  const previewMode = new URLSearchParams(window.location.search).get('preview') === 'true';

  // Redirect streamer to GoLivePage (skip in ?preview=true mode for local testing)
  useEffect(() => {
    if (isOwnStream && !previewMode) {
      navigate(ROUTES.GO_LIVE, { replace: true });
    }
  }, [isOwnStream, previewMode, navigate]);

  // Load profile + live status
  useEffect(() => {
    if (!username) return;
    let cancelled = false;

    const load = async () => {
      let p = null;
      try {
        const profileRes = await authApi.getPublicProfile(username);
        if (cancelled) return;
        p = profileRes.profile || null;
        setProfile(p);
      } catch {
        if (!cancelled) setProfile(null);
        if (!cancelled) setLoading(false);
        return;
      }

      if (p?.id) {
        try {
          const statuses = await streamApi.getLiveStatuses([p.id]);
          if (cancelled) return;
          const entry = Array.isArray(statuses)
            ? statuses.find((s) => String(s.user_id) === String(p.id))
            : null;
          setLiveStatus(entry?.is_live ? entry : null);
          setViewerCount(entry?.live_status?.viewer_count ?? 0);
        } catch {
          // Live status unavailable — page still shows profile
        }
      }

      if (!cancelled) setLoading(false);
    };

    load();
    return () => { cancelled = true; };
  }, [username]);

  // Poll live status while offline so the page auto-updates when streamer goes live
  useEffect(() => {
    const streamerId = profile?.id;
    if (!streamerId || liveStatus) return undefined; // only poll when currently offline

    const checkLive = async () => {
      try {
        const statuses = await streamApi.getLiveStatuses([streamerId]);
        const entry = Array.isArray(statuses)
          ? statuses.find((s) => String(s.user_id) === String(streamerId))
          : null;
        if (entry?.is_live) {
          setLiveStatus(entry);
          setViewerCount(entry?.live_status?.viewer_count ?? 0);
        }
      } catch {}
    };

    const id = setInterval(checkLive, LIVE_STATUS_POLL_MS);
    return () => clearInterval(id);
  }, [profile?.id, liveStatus]);

  // Watch heartbeat
  useEffect(() => {
    const streamerId = profile?.id;
    if (!streamerId || !liveStatus) return undefined;

    const ping = async () => {
      try {
        const res = await streamApi.sendWatchHeartbeat(streamerId);
        if (typeof res?.viewer_count === 'number') setViewerCount(res.viewer_count);
      } catch {}
    };

    ping();
    const id = setInterval(ping, WATCH_HEARTBEAT_MS);
    return () => clearInterval(id);
  }, [profile?.id, liveStatus]);

  const handleLogout = useCallback(async () => {
    await authApi.logout();
    clearStoredAuth();
    navigate(ROUTES.WELCOME);
  }, [navigate]);

  const twitchLogin = useMemo(() => {
    if (!profile) return null;
    if (liveStatus?.platform === 'twitch' && liveStatus.platform_username) {
      return liveStatus.platform_username;
    }
    const platforms = profile.connectedPlatforms || [];
    const twitch = platforms.find((p) => p.platform === 'twitch');
    if (twitch?.platformUsername) return twitch.platformUsername;
    if (String(profile.oauthProvider || '').toLowerCase() === 'twitch') {
      return String(profile.oauthProviderUsername || '').trim() || null;
    }
    return null;
  }, [profile, liveStatus]);

  const avatarUrl = profile?.profilePictureUrl || profile?.oauthProfileImage || null;
  const initial = profile?.username ? profile.username.charAt(0).toUpperCase() : '?';

  if (loading) {
    return (
      <div className="watch-page">
        <HomeNavBar spadesBalance={navbarUser.spades} notificationCount={navbarUser.notifications} userAvatar={navbarUser.avatar} userName={navbarUser.name} onSpadesClick={() => {}} onLogout={handleLogout} minimal />
        <LeftSidebar />
        <main className="watch-page__main"><div className="watch-page__loader"><div className="watch-page__loader-ring" /></div></main>
      </div>
    );
  }

  if (!profile) {
    return (
      <div className="watch-page">
        <HomeNavBar spadesBalance={navbarUser.spades} notificationCount={navbarUser.notifications} userAvatar={navbarUser.avatar} userName={navbarUser.name} onSpadesClick={() => {}} onLogout={handleLogout} minimal />
        <LeftSidebar />
        <main className="watch-page__main"><div className="watch-page__not-found"><h2>Streamer not found</h2></div></main>
      </div>
    );
  }

  return (
    <div className="watch-page">
      <HomeNavBar
        spadesBalance={navbarUser.spades}
        notificationCount={navbarUser.notifications}
        userAvatar={navbarUser.avatar}
        userName={navbarUser.name}
        onSpadesClick={() => {}}
        onLogout={handleLogout}
        minimal
      />
      <LeftSidebar />

      <main className="watch-page__main">
        <div className="watch-page__body">

          {/* ── Left: player + info ── */}
          <div className="watch-page__player-col">
            <div className="watch-page__player-wrap">
              {liveStatus && twitchLogin ? (
                <iframe
                  title={`${username} live stream`}
                  src={`https://player.twitch.tv/?channel=${encodeURIComponent(twitchLogin)}&parent=${encodeURIComponent(parentHost)}&autoplay=true&muted=true`}
                  allowFullScreen
                />
              ) : (
                <div className="watch-page__player-offline">
                  <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M23 7l-7 5 7 5V7z" /><rect x="1" y="5" width="15" height="14" rx="2" ry="2" />
                  </svg>
                  <span>{liveStatus ? 'No Twitch channel connected.' : `${username} is not live right now.`}</span>
                </div>
              )}

            </div>

            {/* Info bar */}
            <div className="watch-page__info-bar">
              <div className="watch-page__streamer-avatar">
                {avatarUrl ? <img src={avatarUrl} alt={username} referrerPolicy="no-referrer" /> : initial}
              </div>
              <div className="watch-page__streamer-info">
                <div className="watch-page__streamer-name">{profile.username}</div>
                {liveStatus && (
                  <div className="watch-page__stream-title">
                    {liveStatus.live_status?.stream_title || 'Live Stream'}
                  </div>
                )}
              </div>
              {liveStatus && (
                <div className="watch-page__viewer-count">
                  <span className="watch-page__viewer-dot" />
                  {viewerCount} watching
                </div>
              )}
            </div>

          </div>

          {/* ── Right: chat + spades + live video request ── */}
          <aside className="watch-page__sidebar">
            <div className="watch-page__sidebar-chat">
              {PER_USER_CHATS_ENABLED && profile.username ? (
                <WatchChatPanel
                  roomId={`user:${profile.username}`}
                  streamerUsername={profile.username}
                  currentUser={currentUser}
                />
              ) : (
                <div style={{ padding: 20, color: '#8b97b0', fontSize: 13 }}>
                  Community chat is unavailable.
                </div>
              )}
            </div>

            {/* Send Spades — only when live and viewing someone else */}
            {liveStatus && !isOwnStream && (
              <div className="watch-page__sidebar-panel watch-page__sidebar-panel--gift">
                <GiftSpadesPanel streamerId={profile.id} streamerUsername={profile.username} />
              </div>
            )}

            {/* Request Live Video — only when live, viewer is logged in, not own stream */}
            {liveStatus && (!isOwnStream || previewMode) && readStoredToken() && (
              <div className="watch-page__sidebar-panel watch-page__sidebar-panel--request">
                <div className="watch-page__request-card">
                  <div className="watch-page__sidebar-panel-header">
                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <polygon points="23 7 16 12 23 17 23 7" />
                      <rect x="1" y="5" width="15" height="14" rx="2" ry="2" />
                    </svg>
                    Request Live Video
                  </div>
                  <VideoRequestButton
                    streamerId={profile.id}
                    streamerUsername={profile.username}
                    onLocalStreamChange={setViewerLocalStream}
                  />
                </div>
              </div>
            )}
          </aside>
        </div>
      </main>
    </div>
  );
}

/* ── Inline Chat Panel ───────────────────────────────────────────────────── */
function WatchChatPanel({ roomId, streamerUsername, currentUser }) {
  const [messages, setMessages] = useState([]);
  const [connectionState, setConnectionState] = useState('disconnected');
  const [draft, setDraft] = useState('');
  const roomRef = useRef(null);
  const displayNameRef = useRef(currentUser?.username || 'Viewer');
  const profileImageRef = useRef(currentUser?.profileImage || '');
  const scrollRef = useRef(null);
  const mergeMessages = useCallback((incoming) => {
    setMessages((prev) => {
      const merged = [...prev];
      const seen = new Set(prev.map((msg) => msg.id));
      incoming.forEach((msg) => {
        if (!seen.has(msg.id)) {
          seen.add(msg.id);
          merged.push(msg);
        }
      });
      return merged;
    });
  }, []);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages.length]);

  useEffect(() => {
    if (!roomId) return undefined;
    let cancelled = false;
    let room = null;
    const cleanups = [];

    const tokenProvider = async () => {
      const resp = await communityApi.issueChatToken(roomId);
      return {
        token: resp.token,
        sessionExpirationTime: resp.sessionExpirationTime ? new Date(resp.sessionExpirationTime) : undefined,
        tokenExpirationTime: resp.tokenExpirationTime ? new Date(resp.tokenExpirationTime) : undefined,
      };
    };

    (async () => {
      try {
        const first = await communityApi.issueChatToken(roomId);
        if (cancelled) return;
        if (first.displayName) displayNameRef.current = first.displayName;
        if (first.profileImage) profileImageRef.current = first.profileImage;

        room = new ChatRoom({ regionOrUrl: first.region || 'us-east-1', tokenProvider });
        roomRef.current = room;

        cleanups.push(room.addListener('connecting', () => setConnectionState('connecting')));
        cleanups.push(room.addListener('connect', async () => {
          setConnectionState('connected');
          try {
            const history = await communityApi.fetchHistory(roomId, { limit: 30 });
            if (cancelled) return;
            const historicalMessages = Array.isArray(history?.messages)
              ? history.messages.map((msg) => ({
                  id: msg.id || `history-${msg.sentAt}-${msg.senderUserId}`,
                  content: msg.content || '',
                  sender: {
                    userId: msg.senderUserId || undefined,
                  },
                  attributes: {
                    displayName: msg.senderDisplayName || undefined,
                    profileImage: msg.senderProfileImage || undefined,
                  },
                  sendTime: msg.sentAt ? new Date(msg.sentAt) : undefined,
                  type: 'EVENT',
                }))
              : [];
            mergeMessages(historicalMessages);
          } catch {}
        }));
        cleanups.push(room.addListener('disconnect', () => setConnectionState('disconnected')));
        cleanups.push(room.addListener('message', (m) => mergeMessages([m])));
        cleanups.push(room.addListener('messageDelete', (e) => setMessages((prev) => prev.filter((m) => m.id !== e.messageId))));

        room.connect();
      } catch {}
    })();

    return () => {
      cancelled = true;
      cleanups.forEach((off) => { try { off(); } catch {} });
      if (room) { try { room.disconnect(); } catch {} }
      roomRef.current = null;
    };
  }, [roomId, mergeMessages]);

  const handleSend = useCallback(async (text) => {
    const trimmed = (text || '').trim();
    if (!trimmed || !roomRef.current) return;
    try {
      const attrs = { displayName: displayNameRef.current || 'Viewer' };
      if (profileImageRef.current) attrs.profileImage = profileImageRef.current;
      await roomRef.current.sendMessage(new SendMessageRequest(trimmed, attrs));
    } catch {}
  }, []);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!draft.trim()) return;
    handleSend(draft);
    setDraft('');
  };

  return (
    <div className="live-chat" style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <div className="live-chat__header">
        <span className="live-chat__title">Community Chat</span>
        <span className={`live-chat__status live-chat__status--${connectionState}`}>
          {connectionState === 'connected' ? 'Connected' : connectionState === 'connecting' ? 'Connecting…' : 'Disconnected'}
        </span>
      </div>
      <div ref={scrollRef} className="live-chat__messages" style={{ flex: 1, overflowY: 'auto' }}>
        {messages.length === 0 && (
          <div className="live-chat__empty">No messages yet. Say hi to {streamerUsername}.</div>
        )}
        {messages.map((m) => {
          const isMine = m.sender?.userId === currentUser?.id;
          const displayName = m.attributes?.displayName
            || (isMine ? currentUser?.username : m.sender?.userId)
            || 'unknown';
          const profileImage = m.attributes?.profileImage
            || (isMine ? currentUser?.avatar : '')
            || '';
          return (
            <div key={m.id} className={`live-chat__msg${isMine ? ' live-chat__msg--mine' : ''}`}>
              <div className="live-chat__msg-avatar" aria-hidden="true">
                {profileImage ? (
                  <img src={profileImage} alt="" referrerPolicy="no-referrer" />
                ) : (
                  <span className="live-chat__msg-avatar-fallback">
                    {displayName.charAt(0).toUpperCase()}
                  </span>
                )}
              </div>
              <div className="live-chat__msg-body">
                <span className="live-chat__msg-name">{displayName}:</span>
                <span className="live-chat__msg-content">{m.content}</span>
              </div>
            </div>
          );
        })}
      </div>
      <form className="live-chat__composer" onSubmit={handleSubmit}>
        <input
          type="text"
          className="live-chat__input"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          placeholder={connectionState === 'connected' ? 'Message' : 'Connecting…'}
          disabled={connectionState !== 'connected'}
        />
        <button type="submit" className="live-chat__send" disabled={connectionState !== 'connected' || !draft.trim()}>
          Send
        </button>
      </form>
    </div>
  );
}

/* ── Inline Gift Spades ──────────────────────────────────────────────────── */
const GIFT_TIERS = [
  { amount: 200, label: '200' },
  { amount: 600, label: '600' },
  { amount: 1000, label: '1K' },
  { amount: 2000, label: '2K' },
];

function GiftSpadesPanel({ streamerId, streamerUsername }) {
  const [selectedTier, setSelectedTier] = useState(null);
  const [customAmount, setCustomAmount] = useState('');
  const [toast, setToast] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const handleGift = async () => {
    const amount = selectedTier || Number(customAmount) || 0;
    if (!amount || !streamerId || submitting) return;
    setSubmitting(true);
    try {
      const result = await authApi.giftSpades({ streamerId, amount });
      setToast(`Sent ${result.spades_sent} spades to @${streamerUsername}!`);
      setSelectedTier(null);
      setCustomAmount('');
    } catch (err) {
      setToast(err?.message || 'Failed to send spades');
    } finally {
      setSubmitting(false);
      setTimeout(() => setToast(''), 3000);
    }
  };

  return (
    <div className="gift-spades">
      <div className="gift-spades__header"><span className="gift-spades__title">Send Spades</span></div>
      <div className="gift-spades__tiers">
        {GIFT_TIERS.map((tier) => (
          <button
            key={tier.amount}
            type="button"
            className={`gift-spades__tier${selectedTier === tier.amount ? ' gift-spades__tier--active' : ''}`}
            onClick={() => { setSelectedTier(tier.amount); setCustomAmount(''); }}
            disabled={submitting}
          >
            ♠ {tier.label}
          </button>
        ))}
      </div>
      <input
        type="number"
        min="1"
        className="gift-spades__custom"
        placeholder="Custom amount"
        value={customAmount}
        onChange={(e) => { setCustomAmount(e.target.value); setSelectedTier(null); }}
        disabled={submitting}
      />
      <button type="button" className="gift-spades__submit" onClick={handleGift} disabled={submitting || !streamerId || (!selectedTier && !Number(customAmount))}>
        {submitting ? 'Sending…' : 'Send'}
      </button>
      {toast && <div className="gift-spades__toast">{toast}</div>}
    </div>
  );
}

export default WatchPage;
